/**
 * Opportunity List Page
 *
 * Displays opportunities pipeline with card/table toggle,
 * status filtering, and search.
 *
 * @module pages/opportunities/OpportunityList
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from '../../utils/toast';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Collapse,
  Dropdown,
  Modal,
} from 'react-bootstrap';
import { DataTable } from '../../components/common/DataTable';
import KebabMenu from '../../components/common/KebabMenu';
import {
  listOpportunities,
  deleteOpportunity,
} from '../../services/opportunityService';
import { formatCurrency, formatDate, formatPercentage } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import DashboardLayout from '../../components/layout/DashboardLayout';

const STATUS_CONFIG = {
  lead: { label: 'Lead', variant: 'secondary', icon: 'bi-record-circle' },
  prospect: { label: 'Prospecto', variant: 'info', icon: 'bi-binoculars' },
  qualified: { label: 'Calificado', variant: 'primary', icon: 'bi-check2-circle' },
  proposal: { label: 'Propuesta', variant: 'warning', icon: 'bi-file-earmark-text' },
  negotiation: { label: 'Negociacion', variant: 'dark', icon: 'bi-chat-dots' },
  won: { label: 'Ganada', variant: 'success', icon: 'bi-trophy' },
  lost: { label: 'Perdida', variant: 'danger', icon: 'bi-x-circle' },
};

const SOURCE_LABELS = {
  referral: 'Referencia',
  website: 'Sitio Web',
  cold_call: 'Llamada',
  trade_show: 'Feria',
  repeat_client: 'Cliente Recurrente',
  other: 'Otro',
};

const OpportunityList = () => {
  const navigate = useNavigate();
  const { isAdmin, isTechnician } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();
  const canEdit = isAdmin || isTechnician;

  const [filters, setFilters] = useState({
    status: '',
    search: '',
    page: 0,
    pageSize: 20,
  });

  const [searchInput, setSearchInput] = useState('');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [oppToDelete, setOppToDelete] = useState(null);

  const searchTimeoutRef = useRef(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['opportunities', filters],
    queryFn: () => listOpportunities(filters),
    placeholderData: (prev) => prev,
  });

  const opportunities = data?.data || [];
  const totalCount = data?.count || 0;

  const oppColumns = useMemo(() => {
    const cols = [
      {
        id: 'title',
        header: 'Titulo',
        accessor: 'title',
        minWidth: 180,
        cell: (opp) => (
          <Button
            variant="link"
            className="p-0 text-start"
            onClick={(e) => { e.stopPropagation(); navigate(`/oportunidades/${opp.id}`); }}
          >
            {opp.title}
          </Button>
        ),
      },
      {
        id: 'client',
        header: 'Cliente',
        accessor: (o) => o.client?.company_name || '',
        minWidth: 160,
        cell: (o) => o.client?.company_name || '-',
      },
      {
        id: 'status',
        header: 'Estado',
        accessor: 'status',
        minWidth: 120,
        cell: (opp) => {
          const sc = STATUS_CONFIG[opp.status] || STATUS_CONFIG.lead;
          return <Badge bg={sc.variant}>{sc.label}</Badge>;
        },
      },
      {
        id: 'estimated_value',
        header: 'Valor Est.',
        accessor: (o) => o.estimated_value || 0,
        align: 'right',
        minWidth: 110,
        cell: (o) => formatCurrency(o.estimated_value),
      },
      {
        id: 'probability',
        header: 'Probabilidad',
        accessor: (o) => o.probability_percentage || 0,
        minWidth: 110,
        cell: (o) => formatPercentage(o.probability_percentage),
      },
      {
        id: 'expected_close',
        header: 'Cierre Esperado',
        accessor: 'expected_close_date',
        minWidth: 130,
        cell: (o) => formatDate(o.expected_close_date),
      },
      {
        id: 'assigned',
        header: 'Asignado',
        accessor: (o) => o.assigned?.full_name || '',
        minWidth: 130,
        cell: (o) => <small className="text-muted">{o.assigned?.full_name || '-'}</small>,
      },
    ];

    if (canEdit) {
      cols.push({
        id: 'actions',
        header: '',
        sortable: false,
        resizable: false,
        align: 'center',
        noSelect: true,
        minWidth: 50,
        maxWidth: 60,
        cell: (opp) => (
          <div data-no-select onClick={(e) => e.stopPropagation()}>
            <KebabMenu label={`Acciones de ${opp.title}`}>
              <Dropdown.Item onClick={() => navigate(`/oportunidades/${opp.id}`)}>
                <i className="bi bi-eye me-2"></i>Ver
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                className="text-danger"
                onClick={() => { setOppToDelete(opp); setShowDeleteModal(true); }}
              >
                <i className="bi bi-trash me-2"></i>Eliminar
              </Dropdown.Item>
            </KebabMenu>
          </div>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, canEdit]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, page: 0 }));
  };

  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value, page: 0 }));
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    return count;
  }, [filters.status]);

  const handleClearFilters = () => {
    setFilters((prev) => ({ ...prev, status: '', search: '', page: 0 }));
    setSearchInput('');
  };

  const handleDelete = async () => {
    if (!oppToDelete) return;
    try {
      const result = await deleteOpportunity(oppToDelete.id);
      if (result.error) throw result.error;
      toast.success('Oportunidad eliminada');
      setShowDeleteModal(false);
      setOppToDelete(null);
      refetch();
    } catch (err) {
      toast.error('Error al eliminar oportunidad');
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" />
        </Container>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error al cargar oportunidades</Alert.Heading>
            <p>{error.message}</p>
            <Button variant="outline-danger" onClick={() => refetch()}>Reintentar</Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">Oportunidades</h2>
                <p className="text-muted">{totalCount} oportunidades</p>
              </div>
              <div className="d-flex gap-2">
                {canEdit && (
                  <Button variant="primary" onClick={() => navigate('/oportunidades/nueva')}>
                    <i className="bi bi-plus-circle me-1"></i>
                    Nueva Oportunidad
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Search & Filters */}
        <div className="mb-4">
          <div className="d-flex gap-2">
            <InputGroup className="flex-grow-1">
              <InputGroup.Text>
                {isFetching && !isLoading ? (
                  <Spinner animation="border" size="sm" className="text-muted" />
                ) : (
                  <i className="bi bi-search"></i>
                )}
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar oportunidades..."
                aria-label="Buscar oportunidades"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </InputGroup>
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
            <Button
              variant={activeFiltersCount > 0 ? 'primary' : 'outline-secondary'}
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            >
              <i className={`bi bi-chevron-${isFiltersExpanded ? 'up' : 'down'}`}></i>
              <span className="d-none d-md-inline ms-1">Filtros</span>
              {activeFiltersCount > 0 && (
                <Badge bg="light" text="dark" pill className="ms-1">{activeFiltersCount}</Badge>
              )}
            </Button>
          </div>

          <Collapse in={isFiltersExpanded}>
            <div>
              <div className="bg-light rounded p-3 mt-2">
                <Row className="g-2">
                  <Col xs={12} sm={6} md={4}>
                    <Form.Label className="small text-muted mb-1">Estado</Form.Label>
                    <Form.Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      size="sm"
                    >
                      <option value="">Todos</option>
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <option key={key} value={key}>{cfg.label}</option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
                {(activeFiltersCount > 0 || filters.search) && (
                  <div className="d-flex justify-content-end mt-2">
                    <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                      <i className="bi bi-x-circle me-1"></i>Limpiar filtros
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Collapse>
        </div>

        {/* Opportunity List */}
        <Row>
          <Col>
            <Card>
              <Card.Body className="p-0">
                {opportunities.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-funnel" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                    <h5 className="mt-3 text-muted">No se encontraron oportunidades</h5>
                    <p className="text-muted">
                      {filters.status || filters.search
                        ? 'Intenta cambiar los filtros'
                        : canEdit
                        ? 'Crea tu primera oportunidad'
                        : 'No hay oportunidades disponibles'}
                    </p>
                  </div>
                ) : isTableView ? (
                  <DataTable
                    tableId="opportunities"
                    data={opportunities}
                    rowKey={(o) => o.id}
                    columns={oppColumns}
                  />
                ) : (
                  <Row className="g-3 p-3">
                    {opportunities.map((opp) => {
                      const sc = STATUS_CONFIG[opp.status] || STATUS_CONFIG.lead;
                      return (
                        <Col xs={12} sm={6} lg={4} key={opp.id}>
                          <Card className="shadow-sm h-100">
                            <Card.Body style={{ position: 'relative' }}>
                              {canEdit && (
                                <KebabMenu
                                  label={`Acciones de ${opp.title}`}
                                  stopPropagation={false}
                                  dropdownProps={{ style: { position: 'absolute', top: '12px', right: '12px' } }}
                                >
                                  <Dropdown.Item onClick={() => navigate(`/oportunidades/${opp.id}`)}>
                                    <i className="bi bi-eye me-2"></i>Ver
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                      setOppToDelete(opp);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    <i className="bi bi-trash me-2"></i>Eliminar
                                  </Dropdown.Item>
                                </KebabMenu>
                              )}

                              <h6
                                className="mb-2 fw-bold pe-4"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/oportunidades/${opp.id}`)}
                              >
                                {opp.title}
                              </h6>

                              <div className="d-flex gap-2 mb-2">
                                <Badge bg={sc.variant}>{sc.label}</Badge>
                                {opp.source && (
                                  <Badge bg="light" text="dark">
                                    {SOURCE_LABELS[opp.source] || opp.source}
                                  </Badge>
                                )}
                              </div>

                              {opp.client?.company_name && (
                                <small className="text-muted d-block mb-2">
                                  <i className="bi bi-building me-1"></i>
                                  {opp.client.company_name}
                                </small>
                              )}

                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-primary fw-bold fs-5">
                                  {formatCurrency(opp.estimated_value)}
                                </span>
                                <Badge bg="light" text="dark">
                                  {formatPercentage(opp.probability_percentage)}
                                </Badge>
                              </div>

                              <div className="d-flex justify-content-between small mt-2 pt-2 border-top">
                                {opp.expected_close_date && (
                                  <span className="text-muted">
                                    <i className="bi bi-calendar3 me-1"></i>
                                    {formatDate(opp.expected_close_date)}
                                  </span>
                                )}
                                {opp.assigned?.full_name && (
                                  <span className="text-muted">
                                    <i className="bi bi-person me-1"></i>
                                    {opp.assigned.full_name}
                                  </span>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Delete Confirmation */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar Eliminacion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Estas seguro de eliminar la oportunidad <strong>{oppToDelete?.title}</strong>?</p>
            <p className="text-danger mb-0">
              <i className="bi bi-exclamation-triangle me-1"></i>
              Esta accion no se puede deshacer.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              <i className="bi bi-trash me-1"></i>Eliminar
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </DashboardLayout>
  );
};

export default OpportunityList;
